import { useMemo, useState } from 'react';
import { ZoneCreationProperties, SubcategoryAllocation } from '../types/zoneMapType';
import { validateEditingZoneData, validatePublishZoneLiveDate } from '../zoneViewer/zoneMapDataHelper';
import { validateAllocations } from '../allocation/validateAllocations';
import { validateDropZones } from '../allocation/validateDropZones';
import { FloorplanFixtureData } from '../zoneViewer/zoneMapDataType';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { useFlowLabel } from '../utils/flowLabel';

const INITIAL_ZONE: ZoneCreationProperties = {
  zoneIdx: 0,
  zoneName: '',
  productGroup: '',
  categoryId: '',
  subcategoryIds: [],
  subcategoryNames: [],
  fixtureIds: [],
  fixtureModels: new Map(),
  facings: 0,
  pickPaths: [],
};

function createInitialMap(): Map<number, ZoneCreationProperties> {
  const map = new Map<number, ZoneCreationProperties>();
  map.set(0, { ...INITIAL_ZONE });
  return map;
}

interface UseCreateZoneStateInput {
  agileFixtures: FloorplanFixtureData[];
  editingFixtureOrderingMap: Map<string, number>;
  fixtureOrderingMap: Map<string, number>;
  switchButtonValue: string | undefined;
  copyToNewDate: string;
}

export function useCreateZoneState({
  agileFixtures,
  editingFixtureOrderingMap,
  fixtureOrderingMap,
  switchButtonValue,
  copyToNewDate,
}: UseCreateZoneStateInput) {
  const config = useProgramConfig();
  const isGfcPublish = config.macroplanogramScope === 'category';
  const requireAllFixturesAssigned = config.zoneMapBehavior.requireAllFixturesAssigned;
  const flowLabel = useFlowLabel();

  const [zoneCreationProperties, setZoneCreationProperties] =
    useState<Map<number, ZoneCreationProperties>>(createInitialMap);
  const [zoneCreationValidationErrors, setZoneCreationValidationErrors] = useState<string[]>([]);
  const [activeZoneIdx, setActiveZoneIdx] = useState<number>(0);

  const initialZoneCreationPropertiesMap = useMemo(() => createInitialMap(), []);

  const updateProductGroup = (zoneIdx: number, productGroup?: string) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, { ...prev.get(zoneIdx)!, productGroup: productGroup || '' });
      return newMap;
    });
  };

  const updateCategoryId = (zoneIdx: number, categoryId?: string) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, { ...prev.get(zoneIdx)!, categoryId: categoryId || '' });
      return newMap;
    });
  };

  const updateSubcategories = (zoneIdx: number, subcategoryIds?: string[], subcategoryNames?: string[]) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, {
        ...prev.get(zoneIdx)!,
        subcategoryIds: subcategoryIds || [],
        subcategoryNames: subcategoryNames || [],
        zoneName: subcategoryNames?.sort().join('/') ?? '',
      });
      return newMap;
    });
  };

  const updateAllocations = (zoneIdx: number, allocations: SubcategoryAllocation[]) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      const subcategoryIds = allocations.map((a) => a.subcategoryId).filter(Boolean);
      const subcategoryNames = allocations.map((a) => a.subcategoryName).filter(Boolean);
      const orderingMap = editingFixtureOrderingMap.size > 0 ? editingFixtureOrderingMap : fixtureOrderingMap;
      const fixtureIds = orderingMap ? Array.from(orderingMap.keys()) : (prev.get(zoneIdx)?.fixtureIds ?? []);
      newMap.set(zoneIdx, {
        ...prev.get(zoneIdx)!,
        allocations,
        subcategoryIds,
        subcategoryNames,
        fixtureIds,
        facings: prev.get(zoneIdx)?.facings || 1,
        zoneName: subcategoryNames.sort().join('/'),
      });
      return newMap;
    });
  };

  const validateZoneMap = (): Set<string> => {
    const publishZoneDateError = validatePublishZoneLiveDate(switchButtonValue, copyToNewDate);
    const errors: Set<string> = new Set([...publishZoneDateError]);

    if (isGfcPublish) {
      // GFC Drop Zone publish: skip subcategory/product-type requirements.
      // Each zone == one Drop Zone == one categoryId, with explicit fixture
      // ownership. Shared with the publish-time gate via validateDropZones.
      validateDropZones(zoneCreationProperties).forEach((err) => errors.add(err));
    } else {
      // Fresh/WFM: full validation including subcategory requirements
      const allowPartialAssignment = !requireAllFixturesAssigned;
      const zoneDataErrors = validateEditingZoneData(zoneCreationProperties, agileFixtures, allowPartialAssignment);
      zoneDataErrors.forEach((err) => errors.add(err));

      // Validate percentage-based allocations
      for (const zone of zoneCreationProperties.values()) {
        if (zone.allocations && zone.allocations.length > 0) {
          const allocationValidation = validateAllocations(zone.allocations);
          if (!allocationValidation.isValid) {
            allocationValidation.errors.forEach((err) => errors.add(`${zone.zoneName || 'Zone'}: ${err}`));
          }
        }
      }

      const hasAnyAllocations = Array.from(zoneCreationProperties.values()).some(
        (zone) => zone.allocations && zone.allocations.some((a) => a.subcategoryId && a.percentage > 0),
      );
      if (hasAnyAllocations) {
        const orderingMap = editingFixtureOrderingMap.size > 0 ? editingFixtureOrderingMap : fixtureOrderingMap;
        const orderedCount = orderingMap?.size ?? 0;
        const totalFixtures = agileFixtures.length;
        if (orderedCount < totalFixtures) {
          errors.add(
            `${flowLabel.sentence} must cover all ${totalFixtures} fixtures (currently ${orderedCount}). Draw the ${flowLabel.lower} through all fixtures before publishing with allocations.`,
          );
        }
      }
    }

    setZoneCreationValidationErrors([...errors]);
    return errors;
  };

  const handleZoneCreationSubmit = (setOpenConfirmationModal: (v: boolean) => void) => {
    if (validateZoneMap().size === 0) {
      setOpenConfirmationModal(true);
    }
  };

  const resetZoneCreation = () => {
    setZoneCreationProperties(createInitialMap());
    setActiveZoneIdx(0);
  };

  return {
    zoneCreationProperties,
    setZoneCreationProperties,
    zoneCreationValidationErrors,
    setZoneCreationValidationErrors,
    activeZoneIdx,
    setActiveZoneIdx,
    initialZoneCreationPropertiesMap,
    updateProductGroup,
    updateCategoryId,
    updateSubcategories,
    updateAllocations,
    validateZoneMap,
    handleZoneCreationSubmit,
    resetZoneCreation,
  };
}
